/**
 * Historial API module.
 *
 * Covers listing audit-log records and undoing reversible actions.
 * Both endpoints require `administrador` role on the backend.
 *
 * All functions delegate to `apiFetch`, which handles auth headers,
 * JSON parsing and Ant Design error/success toasts automatically.
 */

import { apiFetch } from "./apiClient";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface HistorialRecord {
  id: number;
  accion: string;
  informacion?: string;
  resumen?: string;
  quien_realiza?: string;
  reversible: boolean;
  undone_at?: string | null;
  created_at: string;
}

export interface HistorialListParams {
  page?: number;
  size?: number;
  q?: string;
  accion?: string;
  reversible?: boolean;
  undone?: boolean | "true" | "false";
  from?: string;
  to?: string;
}

export interface HistorialListResponse {
  data: HistorialRecord[];
  total: number;
  page: number;
  size: number;
}

// ─── historialApi ─────────────────────────────────────────────────────────────

export const historialApi = {
  /**
   * GET /historial — paginated, filterable list of audit records.
   * Requires `administrador` role.
   */
  list: (
    params?: HistorialListParams,
  ): Promise<HistorialListResponse | null> => {
    const qs = params
      ? "?" +
        new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)]) as [string, string][],
        ).toString()
      : "";
    return apiFetch<HistorialListResponse>(`/historial${qs}`, {
      errorMsg: "Error al obtener el historial",
    });
  },

  /**
   * POST /historial/:id/undo — reverts a reversible audit record.
   * Requires `administrador` role.
   */
  undo: (id: number): Promise<any | null> =>
    apiFetch(`/historial/${id}/undo`, {
      method: "POST",
      successMsg: "Registro revertido correctamente",
      errorMsg: "Error al revertir el registro",
    }),
};
